import {
	reactive,
	Ref,
	ref,
	toRef,
} from '@vue/composition-api';
import { getUserProfileOptions } from '@front-app/services/UserApi/UserApi';
import {
	Credential,
	Interest,
	Specialty,
} from '@front-app/services/UserApi/UserModel';
import { STATUS } from '@front-app/constants/state';
import { useIsLoading } from '@front-app/hooks/useAsyncStatus';
import { STARTABLE_STATUSES } from '../util/cache';

export interface UserProfileOptionsStore {
	getInterestByName: (name: string) => Interest | undefined;
	getSpecialtyByName: (name: string) => Specialty | undefined;
	fetch: () => Promise<void>;
	isLoading: Ref<boolean>;
	specialties: Ref<Specialty[]>;
	interests: Ref<Interest[]>;
	credentials: Ref<Credential[]>;
}

interface UserProfileOptionsState {
	specialties: Specialty[];
	interests: Interest[];
	credentials: Credential[];
}

export const getUserProfileOptionsStore = (): UserProfileOptionsStore => {
	const loadingStatus = ref(STATUS.UNSTARTED);
	const state: UserProfileOptionsState = reactive({
		specialties: [],
		interests: [],
		credentials: [],
	});
	const isLoading = useIsLoading(loadingStatus);
	const fetch = async (force = false) => {
		if (!force && !STARTABLE_STATUSES.includes(loadingStatus.value)) {
			return;
		}

		loadingStatus.value = STATUS.LOADING;

		try {
			const {
				specialties,
				interests,
				credentials,
			} = await getUserProfileOptions();

			state.specialties = specialties;
			state.interests = interests;
			state.credentials = credentials;

			loadingStatus.value = STATUS.SUCCESS;
		} catch (e) {
			loadingStatus.value = STATUS.ERROR;
		}
	};

	const getInterestByName = (wantedName: string) => state.interests.find(({ name }) => name === wantedName);
	const getSpecialtyByName = (wantedName: string) => state.specialties.find(({ name }) => name === wantedName);

	return {
		getInterestByName,
		getSpecialtyByName,
		fetch,
		isLoading,
		specialties: toRef(state, 'specialties'),
		interests: toRef(state, 'interests'),
		credentials: toRef(state, 'credentials'),
	};
};
